import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../services/apiService';
import { CafeStatus, StatusMessages, Organization } from '../types';

// Define the organization data structure
export interface OrganizationData {
  id: number;
  name: string;
  urlName: string;
  createdBy: string;
  createdTime: string;
  deleted?: boolean;
  cafeStatus: CafeStatus;
  statusMessages: StatusMessages;
}

interface OrganizationState {
  data: OrganizationData | null;
  loading: boolean;
  error: string | null;
  lastUpdated: number | null;
}

const initialState: OrganizationState = {
  data: null,
  loading: false,
  error: null,
  lastUpdated: null,
};

// Async thunk to fetch organization data
export const fetchOrganizationData = createAsyncThunk<OrganizationData, string, { rejectValue: string }>(
  'organization/fetchData',
  async (orgSlug: string, { rejectWithValue }) => {
    try {
      const response = await apiService.getPublicOrganization(orgSlug);
      if (response.success && response.data && response.data.organization) {
        const org = response.data.organization;
        return {
          id: org.id,
          name: org.name,
          urlName: org.urlName,
          createdBy: org.createdBy,
          createdTime: org.createdTime,
          deleted: org.deleted,
          cafeStatus: org.cafeStatus || { isOpen: false, isBusy: false },
          statusMessages: org.statusMessages || {
            closed: "We're currently closed. Please visit us during our business hours.",
            busy: "We're experiencing high volume. Please visit our counter for faster service.",
            open: "Welcome! You can place your order below."
          },
        };
      } else {
        return rejectWithValue(response.error || 'Failed to fetch organization data');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch organization data');
    }
  }
);

const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {
    clearOrganizationData: (state) => {
      state.data = null;
      state.error = null;
      state.lastUpdated = null;
    },
    updateCafeStatus: (state, action: PayloadAction<CafeStatus>) => {
      if (state.data) {
        state.data.cafeStatus = action.payload;
        state.lastUpdated = Date.now();
      }
    },
    updateStatusMessages: (state, action: PayloadAction<StatusMessages>) => {
      if (state.data) {
        state.data.statusMessages = action.payload;
        state.lastUpdated = Date.now();
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrganizationData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;
        state.lastUpdated = Date.now();
      })
      .addCase(fetchOrganizationData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearOrganizationData, updateCafeStatus, updateStatusMessages } = organizationSlice.actions;
export default organizationSlice.reducer;
